import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MaterialCategory } from '@/types';
import { categories, MaterialFormData } from './forms/MaterialFormData';
import { useMaterialSubmit } from './hooks/useMaterialSubmit';
import { DatabaseMaterial } from '@/hooks/useMaterials';
import BoardMaterialForm from './forms/BoardMaterialForm';
import FrameMaterialForm from './forms/FrameMaterialForm';
import GuideMaterialForm from './forms/GuideMaterialForm';
import OtherMaterialForm from './forms/OtherMaterialForm';
import ScrewMaterialForm from './forms/ScrewMaterialForm';
import InsulationMaterialForm from './forms/InsulationMaterialForm';
import AccessoryMaterialFormEdit from './forms/AccessoryMaterialFormEdit';
import MaterialPriceHistorySparkline from './MaterialPriceHistorySparkline';
import { TrendingUp, Info, AlertTriangle } from 'lucide-react';
import { missingRequiredFields } from '@/lib/materials/taxonomy';
import MaterialCustomizationCard from './MaterialCustomizationCard';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useIsAppAdmin } from '@/hooks/useIsAppAdmin';

interface EditMaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  material: DatabaseMaterial;
}

const EditMaterialDialog = ({ open, onOpenChange, onSuccess, material }: EditMaterialDialogProps) => {
  const [formData, setFormData] = useState<MaterialFormData>({
    code: '',
    name: '',
    description: '',
    category: 'board' as MaterialCategory,
    supplier: '',
    thickness: '',
    width: '',
    length: '',
    weight_per_sqm: '',
    unit_price: '',
    unit: 'mq',
    thermal_conductivity: '',
    acoustic_performance: '',
    fire_resistance_class: '',
    color_hex: '#CCCCCC',
    incidence_base: '',
    incidence_per_sqm: '1',
    passo: '600',
    installation_time_per_sqm: '',
    material_type: 'gesso_rivestito',
    board_typology: 'standard',
    density: '',
    flexural_strength: '',
    surface_hardness: '',
    en_520_type: '',
    water_absorption: '',
    humidity_resistance_class: '',
    environmental_certification: '',
    recycled_content: '',
    voc_class: '',
    rei_compatible: 'false',
    intended_use: '',
    installation_notes: '',
    fire_class: '',
    fire_description: '',
    board_type: '',
    fire_usage_notes: '',
    sheet_thickness: '0.6',
    weight_per_ml: '',
    profile_type: 'C',
    surface_finish: 'zincatura_z140',
    discount: '',
    extra_discount: '0',
    list_price: '',
    is_variable_thickness: false,
    mechanical_performance: '',
    thermal_performance_notes: '',
    sustainability_notes: '',
    system_compatibility: '',
    fire_performance_notes: '',
    carbon_footprint: '',
    epd: '',
    vapor_permeability: '',
    thermal_capacity: '',
    box_pieces: '',
    compatible_board_types: '',
    waste_percentage: '',
    disposal_percentage: '4',
  });
  
  const { submitMaterial, isSubmitting } = useMaterialSubmit(onOpenChange, onSuccess);
  const { currentOrganizationId } = useCurrentOrganization();
  const { isAdmin: isAppAdmin } = useIsAppAdmin();
  // Materiale del catalogo condiviso (org NULL): i suoi dati appartengono a tutte
  // le aziende → non modificabili qui. L'org personalizza prezzo/tempo col delta.
  const isCatalogMaterial = !material?.organization_id;
  /**
   * L'admin di piattaforma (riga in `public.app_admins`) puo` correggere il dato
   * ufficiale del catalogo.
   *
   * Prima il form si disabilitava sul solo `isCatalogMaterial`, senza mai
   * chiedere chi fosse l'utente: un materiale globale risultava non modificabile
   * da NESSUNO, nemmeno dall'unico amministratore della piattaforma, che quindi
   * per correggere un tempo di posa sbagliato nel catalogo doveva passare da
   * SQL. La RLS invece lo prevede da sempre (policy "Admin can update any
   * material", `is_app_admin()`): era solo la UI a fermarlo.
   */
  const canEditCatalog = isCatalogMaterial && isAppAdmin;
  const isReadOnly = isCatalogMaterial && !isAppAdmin;
  const isOwnMaterial = !!material?.organization_id && material.organization_id === currentOrganizationId;
  // Il calcolo unit_price ora vive in MaterialPricingSection: list_price × catena famiglia × (1 - extra/100).

  // Popola form con campo installation_time_per_sqm quando dialog apre
  useEffect(() => {
    if (material && open) {
      // `materials_with_pricing` restituisce i valori GIA` EFFETTIVI per l'org:
      // su quattro campi (prezzo netto, tempo, sfrido, smaltimento) fa COALESCE
      // con `org_material_customizations`. Comodo per calcolare, pericoloso per
      // scriverli: quando l'admin modifica il catalogo condiviso deve partire
      // dai valori di CATALOGO, altrimenti un Salva pubblicherebbe a tutte le
      // aziende il prezzo riservato della sua.
      const catTime = canEditCatalog && material.catalog_installation_time_per_sqm != null
        ? material.catalog_installation_time_per_sqm
        : material.installation_time_per_sqm;
      const catPrice = canEditCatalog && material.catalog_net_price != null
        ? material.catalog_net_price
        : material.unit_price;
      const catWaste = canEditCatalog ? material.catalog_waste_percentage : material.waste_percentage;
      const catDisposal = canEditCatalog
        ? material.catalog_disposal_percentage
        : material.disposal_percentage;
      setFormData({
        code: material.code || '',
        name: material.name || '',
        description: material.description || '',
        category: material.category as MaterialCategory,
        supplier: material.supplier || '',
        // F-family-2026-05-22: pre-popola famiglia se il materiale ne ha una
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        family_id: (material as any).family_id ?? null,
        thickness: material.thickness?.toString() || '',
        width: material.width?.toString() || '',
        length: material.length?.toString() || '',
        weight_per_sqm: material.weight_per_sqm?.toString() || '',
        unit_price: catPrice?.toString() || '',
        unit: material.unit || 'mq',
        thermal_conductivity: material.thermal_conductivity?.toString() || '',
        acoustic_performance: material.acoustic_performance?.toString() || '',
        fire_resistance_class: material.fire_resistance_class || '',
        color_hex: material.color_hex || '#CCCCCC',
        incidence_base: material.incidence_base?.toString() || '',
        incidence_per_sqm: material.incidence_per_sqm?.toString() || '1',
        passo: material.passo?.toString() || '600',
        // DB in ORE → form in MINUTI (×60). Vedi useMaterialSubmit per la conversione inversa.
        installation_time_per_sqm: catTime != null
          ? (Math.round(catTime * 60 * 1e4) / 1e4).toString()
          : '',
        // Niente ripiego inventato: un campo vuoto sul database deve restare
        // vuoto anche nel form. Prima un isolante senza tipo diventava
        // 'gesso_rivestito' e, siccome quel valore non e' fra le opzioni del
        // menu, a video compariva "Lana di vetro": bastava riaprire la scheda
        // per correggere un prezzo e un pannello di lana veniva salvato come
        // gesso rivestito. Sono 102 isolanti su 201.
        material_type: material.material_type || '',
        board_typology: material.board_typology || '',
        density: material.density?.toString() || '',
        flexural_strength: material.flexural_strength || '',
        surface_hardness: material.surface_hardness || '',
        en_520_type: material.en_520_type || '',
        water_absorption: material.water_absorption || '',
        humidity_resistance_class: material.humidity_resistance_class || '',
        environmental_certification: material.environmental_certification || '',
        recycled_content: material.recycled_content?.toString() || '',
        voc_class: material.voc_class || '',
        rei_compatible: material.rei_compatible?.toString() || 'false',
        intended_use: Array.isArray(material.intended_use) ? material.intended_use.join(', ') : (material.intended_use || ''),
        installation_notes: material.installation_notes || '',
        fire_class: material.fire_class || '',
        fire_description: material.fire_description || '',
        board_type: material.board_type || '',
        fire_usage_notes: material.fire_usage_notes || '',
        // Stessa regola dei campi sopra: vuoto resta vuoto. Erano 152 profili a
        // cui la scheda mostrava un lamierino da 0,6 mai dichiarato, e 608 a cui
        // scriveva un rivestimento "zincatura_z140" inventato — che per giunta
        // avrebbe fatto passare per zincato normale un profilo Zinco-Magnesio,
        // perche' il campo viene letto prima del nome.
        sheet_thickness: material.sheet_thickness?.toString() || '',
        weight_per_ml: material.weight_per_ml?.toString() || '',
        profile_type: material.profile_type || '',
        surface_finish: material.surface_finish || '',
        discount: material.discount != null ? material.discount.toString() : '',
        extra_discount: material.extra_discount != null ? material.extra_discount.toString() : '0',
        family_discount_chain_display:
          Array.isArray(material.family_discount_chain) && material.family_discount_chain.length > 0
            ? material.family_discount_chain.map(d => `${d}%`).join(' + ')
            : '',
        family_discount_pct_display: material.family_discount_pct != null ? String(material.family_discount_pct) : '0',
        total_discount_pct_display: material.total_discount_pct != null ? String(material.total_discount_pct) : '0',
        list_price: material.list_price != null ? material.list_price.toString() : (material.unit_price?.toString() || ''),
        is_variable_thickness: material.is_variable_thickness ?? false,
        mechanical_performance: material.mechanical_performance || '',
        thermal_performance_notes: material.thermal_performance_notes || '',
        sustainability_notes: material.sustainability_notes || '',
        system_compatibility: material.system_compatibility || '',
        fire_performance_notes: material.fire_performance_notes || '',
        carbon_footprint: material.carbon_footprint || '',
        epd: material.epd || '',
        vapor_permeability: material.vapor_permeability || '',
        thermal_capacity: material.thermal_capacity || '',
        box_pieces: material.box_pieces != null ? material.box_pieces.toString() : '',
        compatible_board_types: 
          Array.isArray(material.compatible_board_types)
            ? material.compatible_board_types.join(', ')
            : (material.compatible_board_types || ''),
        waste_percentage: catWaste != null ? catWaste.toString() : '',
        disposal_percentage: catDisposal?.toString() || '4',
      });
    }
  }, [material, open, canEditCatalog]);

  const handleCategoryChange = (category: MaterialCategory) => {
    setFormData(prev => ({ ...prev, category }));
  };

  const incompleteFields = missingRequiredFields(
    formData as unknown as Record<string, unknown>,
    formData.category,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Create a copy of formData with id added for update
    const materialData = { ...formData, id: material.id, isCatalogMaterial: canEditCatalog };
    await submitMaterial(materialData);
  };

  const renderCategoryForm = () => {
    switch (formData.category) {
      case 'board':
        return (
          <BoardMaterialForm 
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
          />
        );
      case 'structure_frame':
        return (
          <FrameMaterialForm 
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
          />
        );
      case 'structure_guide':
        return (
          <GuideMaterialForm 
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
          />
        );
      case 'insulation':
        return (
          <InsulationMaterialForm 
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
          />
        );
      case 'accessory':
      case 'finish':
        // 'finish' (stucchi, garze, primer, nastro carta) usa lo stesso form
        // generico di 'accessory' — entrambi sono "consumabili" con qty/m² e
        // confezione (box_pieces). Senza questo case 'finish' cadeva nel
        // default e mostrava "Categoria non supportata".
        return (
          <AccessoryMaterialFormEdit
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
          />
        );
      case 'screw':
        return (
          <ScrewMaterialForm
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
          />
        );
      case 'other':
        return (
          <OtherMaterialForm 
            formData={formData}
            setFormData={setFormData}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return (
          <div className="text-muted-foreground p-8 text-center">
            Categoria non supportata per la modifica
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica Materiale</DialogTitle>
          <DialogDescription>
            Modifica le informazioni del materiale "{material?.name}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Materiali di catalogo: personalizzazione per-org + dati in sola lettura */}
          {isCatalogMaterial && (
            <>
              <MaterialCustomizationCard material={material} onSaved={onSuccess} />
              {isReadOnly ? (
                <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    I dati qui sotto sono quelli <strong>ufficiali del catalogo</strong> (condivisi con
                    tutte le aziende) e non sono modificabili. Per un prodotto completamente tuo,
                    usa <strong>Duplica</strong> dalla tabella materiali: la copia sarà della tua
                    azienda e potrai modificarla liberamente.
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                  <span>
                    Stai modificando il dato <strong>ufficiale del catalogo</strong> come
                    amministratore della piattaforma: quello che salvi qui vale per{' '}
                    <strong>tutte le aziende</strong>, non solo per la tua. I campi partono dai
                    valori di catalogo, non dalle tue personalizzazioni. Per un valore solo tuo usa
                    la card qui sopra.
                  </span>
                </div>
              )}
            </>
          )}

          {/* Selezione Categoria */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Categoria Materiale</CardTitle>
            </CardHeader>
            <CardContent>
              <select
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value as MaterialCategory)}
                className="w-full px-3 py-2 border border-input rounded-md"
                disabled={isSubmitting || isReadOnly}
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </CardContent>
          </Card>

          {/* Campi di classificazione mancanti: si segnalano, non si bloccano.
              Servono ai filtri, ma pretenderli qui vorrebbe dire compilarne tre
              anche solo per correggere un prezzo. */}
          {!isReadOnly && incompleteFields.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                Da completare per rendere il materiale filtrabile:{' '}
                <strong>{incompleteFields.map(f => f.label).join(', ')}</strong>.
                Puoi salvare lo stesso.
              </span>
            </div>
          )}

          {/* Form specifico per categoria — sola lettura sui materiali di catalogo */}
          <fieldset disabled={isReadOnly} className={isReadOnly ? 'opacity-70' : undefined}>
            {renderCategoryForm()}
          </fieldset>

          {/* Storico prezzi (chart sparkline + trend) */}
          {material?.id && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  Storico prezzi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MaterialPriceHistorySparkline
                  materialId={material.id}
                  width={'100%'}
                  height={60}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Ogni variazione di prezzo (listino, netto, sconto extra) viene registrata automaticamente.
                </p>
              </CardContent>
            </Card>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {isReadOnly ? 'Chiudi' : 'Annulla'}
            </Button>
            {/* Sui materiali di catalogo il salvataggio diretto è riservato
                all'admin di piattaforma (policy RLS "Admin can update any
                material") → gli altri usano la card personalizzazione. */}
            {!isReadOnly && (
              <Button type="submit" disabled={isSubmitting} variant={canEditCatalog ? 'destructive' : 'default'}>
                {isSubmitting
                  ? 'Salvando...'
                  : canEditCatalog
                    ? 'Salva nel catalogo (tutte le aziende)'
                    : 'Salva Modifiche'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMaterialDialog;
